import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CreateCampaignResponseDto } from "./types";

interface CampaignState {
  currentStep: number;
  selectedCollectionIds: string[];
  selectAllForUncheck: boolean;
  campaignType?: string;
  campaignName: string;
  createCampaignResponse?: CreateCampaignResponseDto;
  emailTemplateData: EmailTemplate[];
  mailBoxStates: any;
  campaignListWithDetails: any;
  customizations: Customization;
  campaignDetailRecipients: CampaignDetailRecipients;
  campaignId: string;
  domainSettings: any;
  letterTemplateData: any;
  type?: string;
  isTemplate?: boolean;
  isArchived?: boolean;
  isTemplateRefreshed: boolean;
  templateGeneratorPreferences: {
    theme: string;
    differentiators: string[];
    tone: number;
    emotion: number;
    includeLetter: boolean;
  };
}

type Recipient = {
  email: string;
  firstName: string;
  lastName: string;
  statuses: string[];
  emailsSent: number;
  companyName?: string | null;
  company?: {
    companyNumber: string;
    companyName: string;
  };
};

type CampaignDetailRecipients = {
  id: string;
  name: string;
  totalRecipients: number;
  totalEmails: number;
  totalEmailsSent: number;
  totalLettersSent: number;
  totalLetters: number;
  recipients: Recipient[];
};

interface EmailTemplate {
  body: string;
  name: string;
  sendAfterDays: number;
  subject: string;
  stepOrder: number;
}

interface Customization {
  holdingCompany: string;
  phoneNumber: string;
  website: string;
}

interface MailboxState {
  burnerDomains: string[];
  mailboxes: string[];
  preferredDomain: string;
  domain?: string;
}

const initialState: CampaignState = {
  currentStep: 1,
  campaignId: "",
  selectedCollectionIds: [],
  selectAllForUncheck: false,
  campaignType: "",
  campaignName: "New Campaign",
  createCampaignResponse: undefined,
  emailTemplateData: [],
  isTemplate: false,
  isArchived: false,
  mailBoxStates: {
    preferredDomain: "",
  },
  campaignListWithDetails: [],
  customizations: {
    holdingCompany: "",
    phoneNumber: "",
    website: "",
  },
  campaignDetailRecipients: {
    id: "",
    name: "",
    totalRecipients: 0,
    totalEmails: 0,
    totalEmailsSent: 0,
    totalLettersSent: 0,
    totalLetters: 0,
    recipients: [],
  },
  domainSettings: null,
  letterTemplateData: null,
  isTemplateRefreshed: false,

  templateGeneratorPreferences: {
    theme: "",
    differentiators: [],
    tone: 50,
    emotion: 50,
    includeLetter: true,
  },
};

export const campaignSlice = createSlice({
  name: "campaign",
  initialState: initialState,
  reducers: {
    updateCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },

    nextStep: (state) => {
      if (state.currentStep < 6) state.currentStep += 1;
    },

    prevStep: (state) => {
      if (state.currentStep > 1) state.currentStep -= 1;
    },

    resetStep: (state) => {
      state.currentStep = 1;
    },

    setCampaignType: (state, action: PayloadAction<string>) => {
      state.campaignType = action.payload;
    },

    setCampaignName: (state, action: PayloadAction<string>) => {
      state.campaignName = action.payload;
    },

    setSelectedCollectionIds: (state, action: PayloadAction<string[]>) => {
      state.selectedCollectionIds = action.payload;
    },

    toggleCollectionId: (state, action: PayloadAction<string>) => {
      const index = state.selectedCollectionIds?.indexOf(action.payload);
      if (index >= 0) {
        state.selectedCollectionIds.splice(index, 1);
      } else {
        state.selectedCollectionIds.push(action.payload);
      }
    },

    setSelectAllClickedForUncheck: (state, action: PayloadAction<boolean>) => {
      state.selectAllForUncheck = action.payload;
    },

    setCreateCampaignResponse: (
      state,
      action: PayloadAction<CreateCampaignResponseDto>
    ) => {
      state.createCampaignResponse = action.payload;
    },

    setEmailTemplates: (state, action: PayloadAction<EmailTemplate[]>) => {
      state.emailTemplateData = action.payload;
    },

    setLetterTemplate: (state, action: PayloadAction<any>) => {
      state.letterTemplateData = action.payload;
    },

    setCustomizations: (state, action: PayloadAction<Customization>) => {
      state.customizations = action.payload;
    },

    setTemplateRefreshed: (state, action: PayloadAction<boolean>) => {
      state.isTemplateRefreshed = action.payload;
    },

    updateMailboxData: (
      state,
      action: PayloadAction<Partial<MailboxState>>
    ) => {
      const { domain, ...restOfPayload } = action.payload;
      state.mailBoxStates = {
        ...state.mailBoxStates,
        ...restOfPayload,
      };
    },

    resetCampaignState(state) {
      const campaignType = state.campaignType;
      return {
        ...initialState,
        campaignType,
      };
    },

    setCampaignListWithDetails: (state, action: PayloadAction<any>) => {
      state.campaignListWithDetails = action.payload;
    },

    setCampaignDetailsRecipients: (
      state,
      action: PayloadAction<CampaignDetailRecipients>
    ) => {
      state.campaignDetailRecipients = action.payload;
    },

    setCampaignId: (state, action: PayloadAction<string>) => {
      state.campaignId = action.payload;
    },

    setDomainSettings: (state, action: PayloadAction<any>) => {
      state.domainSettings = action.payload[0];
    },

    setCampaignIsArchived: (state, action: PayloadAction<boolean>) => {
      state.isArchived = action.payload;
    },

    setCampaignIsTemplate: (state, action: PayloadAction<boolean>) => {
      state.isTemplate = action.payload;
    },

    setTemplateGeneratorPreferences: (
      state,
      action: PayloadAction<
        Partial<CampaignState["templateGeneratorPreferences"]>
      >
    ) => {
      state.templateGeneratorPreferences = {
        ...state.templateGeneratorPreferences,
        ...action.payload,
      };
    },

    resetTemplateGeneratorPreferences: (state) => {
      state.templateGeneratorPreferences = {
        theme: "",
        differentiators: [],
        tone: 50,
        emotion: 50,
        includeLetter: true,
      };
    },
  },
});

export const {
  updateCurrentStep,
  nextStep,
  prevStep,
  resetStep,
  setSelectedCollectionIds,
  toggleCollectionId,
  setSelectAllClickedForUncheck,
  setCampaignType,
  setCampaignName,
  setCreateCampaignResponse,
  setEmailTemplates,
  setLetterTemplate,
  updateMailboxData,
  resetCampaignState,
  setCampaignListWithDetails,
  setCustomizations,
  setCampaignDetailsRecipients,
  setCampaignId,
  setDomainSettings,
  setCampaignIsArchived,
  setCampaignIsTemplate,
  setTemplateRefreshed,
  setTemplateGeneratorPreferences,
  resetTemplateGeneratorPreferences,
} = campaignSlice.actions;

export default campaignSlice.reducer;
export const selectedCollection = (state: { campaign: CampaignState }) =>
  state.campaign.selectedCollectionIds;

export const selectSelectAllForUncheck = (state: { campaign: CampaignState }) =>
  state.campaign.selectAllForUncheck;

export const selectCampaignType = (state: { campaign: CampaignState }) =>
  state.campaign.campaignType;

export const selectCampaignName = (state: { campaign: CampaignState }) =>
  state.campaign.campaignName;

export const selectCurrentStep = (state: { campaign: CampaignState }) =>
  state.campaign.currentStep;

export const selectCreateCampaignResponse = (state: {
  campaign: CampaignState;
}) => state.campaign.createCampaignResponse;

export const selectEmailTemplate = (state: { campaign: CampaignState }) =>
  state.campaign.emailTemplateData;

export const selectMailboxData = (state: { campaign: CampaignState }) =>
  state.campaign.mailBoxStates;

export const selectCampaignList = (state: { campaign: CampaignState }) =>
  state.campaign.campaignListWithDetails;

export const selectCustomizations = (state: { campaign: CampaignState }) => {
  return state.campaign.customizations;
};

export const selectCampaignDetailRecipients = (state: {
  campaign: CampaignState;
}) => {
  return state.campaign.campaignDetailRecipients;
};
export const selectCampaignId = (state: { campaign: CampaignState }) => {
  return state.campaign.campaignId;
};

export const selectDomainSettings = (state: { campaign: CampaignState }) => {
  return state.campaign.domainSettings;
};

export const selectLetterTemplate = (state: { campaign: CampaignState }) => {
  return state.campaign.letterTemplateData;
};
export const selectCampaignIsArchived = (state: {
  campaign: CampaignState;
}) => {
  return state.campaign.isArchived;
};

export const selectCampaignIsTemplate = (state: { campaign: CampaignState }) =>
  state.campaign.isTemplate;

export const selectTemplateRefreshed = (state: { campaign: CampaignState }) =>
  state.campaign.isTemplateRefreshed;

export const selectSavedPreferencesForGenerateTemplate = (state: {
  campaign: CampaignState;
}) => state.campaign.templateGeneratorPreferences;
